import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { DEVICE_CATALOG } from './catalog';
import {
  signInAnonymously, onAuthStateChanged,
  GoogleAuthProvider, signInWithPopup, signOut
} from 'firebase/auth';
import {
  Camera, Mic2, X, MousePointer2,
  Eraser, ZoomIn, ZoomOut, Hand,
  LogOut, HardDrive, Plus, RotateCcw,
  Package, Search, Download, Upload, FileDown, CloudOff, CalendarDays, FolderOpen, MapPin, Cable
} from 'lucide-react';
import { auth } from './services/firebase';
import {
  createWorkspace,
  DEFAULT_PROJECT_ID,
  listLocalProjects,
  loadLocalWorkspace,
  saveWorkspace,
  subscribeProjectList,
  subscribeWorkspace,
} from './services/workspaceRepository';
import { checkConnection } from './domain/avRules';
import { findAssetConflicts } from './domain/assetConflicts';
import { getRequiredCable, summarizeCableNeeds } from './domain/cableRequirements';
import { createAsset, loadInventory, saveInventory, subscribeInventory } from './services/inventoryRepository';
import { CABLE_TYPES, createCable, loadCableInventory, saveCableInventory, subscribeCableInventory } from './services/cableInventoryRepository';
import { createCustomDevice, loadCustomCatalog, saveCustomCatalog, subscribeCustomCatalog, submitCustomDevice } from './services/customCatalogRepository';
import WarehouseView from './components/WarehouseView';
import { dictionary, LANGUAGE_OPTIONS } from './i18n';

// ─── Firebase ─────────────────────────────────────────────────────────────────
// ─── Inventory (localStorage) ─────────────────────────────────────────────────
// ─── Formaty projektu ─────────────────────────────────────────────────────────
const PROJECT_FORMATS = [
  { id: '720p25',   label: '720p 25',     res: '1280×720',  fps: 25,  note: 'HD PAL' },
  { id: '720p50',   label: '720p 50',     res: '1280×720',  fps: 50,  note: 'HD sport EU' },
  { id: '720p60',   label: '720p 60',     res: '1280×720',  fps: 60,  note: 'HD sport US' },
  { id: '1080i50',  label: '1080i 50',    res: '1920×1080', fps: 25,  note: 'Full HD interlace — broadcast EU (50 pól/s = 25 kl.)' },
  { id: '1080i60',  label: '1080i 60',    res: '1920×1080', fps: 30,  note: 'Full HD interlace — broadcast US (60 pól/s = 30 kl.)' },
  { id: '1080p25',  label: '1080p 25',    res: '1920×1080', fps: 25,  note: 'Full HD kino EU' },
  { id: '1080p30',  label: '1080p 30',    res: '1920×1080', fps: 30,  note: 'Full HD kino US' },
  { id: '1080p50',  label: '1080p 50',    res: '1920×1080', fps: 50,  note: 'Full HD sport EU' },
  { id: '1080p60',  label: '1080p 60',    res: '1920×1080', fps: 60,  note: 'Full HD sport US / streaming' },
  { id: 'uhd4k25',  label: 'UHD 4K 25',  res: '3840×2160', fps: 25,  note: '4K streaming / kino EU' },
  { id: 'uhd4k30',  label: 'UHD 4K 30',  res: '3840×2160', fps: 30,  note: '4K streaming / kino US' },
  { id: 'uhd4k50',  label: 'UHD 4K 50',  res: '3840×2160', fps: 50,  note: '4K sport EU' },
  { id: 'uhd4k60',  label: 'UHD 4K 60',  res: '3840×2160', fps: 60,  note: '4K sport US' },
  { id: 'dci4k24',  label: '4K DCI 24',  res: '4096×2160', fps: 24,  note: '4K DCI — kino cyfrowe' },
  { id: 'dci4k25',  label: '4K DCI 25',  res: '4096×2160', fps: 25,  note: '4K DCI — kino EU' },
  { id: 'dci4k60',  label: '4K DCI 60',  res: '4096×2160', fps: 60,  note: '4K DCI — premium' },
];

// Ranking przepustowości (wyższy = więcej pasma)
const FORMAT_RANK = {
  '720p25': 1, '720p50': 2, '720p60': 3,
  '1080i50': 4, '1080i60': 4,
  '1080p25': 5, '1080p30': 6, '1080p50': 7, '1080p60': 8,
  'uhd4k25': 9, 'uhd4k30': 10, 'uhd4k50': 11, 'uhd4k60': 12,
  'dci4k24': 9, 'dci4k25': 10, 'dci4k60': 13,
};

// Maksymalny format jaki może przenieść dany typ portu
const PORT_MAX_FORMAT = {
  'SDI':         '1080i50',  // HD-SDI 1.485 Gbps — 1080i max
  'SDI-3G':      '1080p60',  // 3G-SDI 2.97 Gbps — 1080p60 max
  'SDI-6G':      'uhd4k30',  // 6G-SDI 5.94 Gbps — UHD 4K 30p max
  'SDI-12G':     'uhd4k60',  // 12G-SDI 11.88 Gbps — UHD 4K 60p max
  'HDMI':        'uhd4k60',  // HDMI 2.0 18 Gbps — UHD 4K 60p
  'DisplayPort': 'dci4k60',  // DP 1.4 32 Gbps — 4K DCI 60p
  'HDBaseT':     'uhd4k60',  // HDBaseT 2.0 — UHD 4K 60p (4:2:0)
  'NDI':         'dci4k60',  // NDI — sieć, praktycznie bez limitu
  'DVI-D':       '1080p60',  // DVI Dual Link
  'VGA':         '1080p60',  // VGA analog
  'Composite':   '720p25',   // Composite/CVBS — SD max
};

// Grupy fizycznej kompatybilności — tylko porty z tej samej grupy można łączyć
// ─── Stałe ────────────────────────────────────────────────────────────────────
const CABLE_COLORS = [
  { name: 'Niebieski', value: '#3b82f6' }, { name: 'Zielony', value: '#10b981' },
  { name: 'Czerwony', value: '#ef4444' }, { name: 'Żółty', value: '#f59e0b' },
  { name: 'Fioletowy', value: '#8b5cf6' }, { name: 'Biały', value: '#f8fafc' }
];

const CATEGORIES = [
  { id: 'all',        label: 'Wszystkie' },
  { id: 'video',      label: 'Video'     },
  { id: 'audio',      label: 'Audio'     },
  { id: 'network-av', label: 'Network AV'},
  { id: 'display',    label: 'Display'   },
];

const CAT_BADGE = {
  video:       'bg-blue-900/40 text-blue-400 border border-blue-800/50',
  audio:       'bg-green-900/40 text-green-400 border border-green-800/50',
  'network-av':'bg-purple-900/40 text-purple-400 border border-purple-800/50',
  display:     'bg-orange-900/40 text-orange-400 border border-orange-800/50',
};

// ─── MemoLink ─────────────────────────────────────────────────────────────────
const MemoLink = memo(({ link, getPortPos, warning, missingCable, onClick }) => {
  const s = getPortPos(link.fromNode, link.fromPort, 'out');
  const e = getPortPos(link.toNode, link.toPort, 'in');
  const dx = Math.abs(e.x - s.x) * 0.5;
  const mx = (s.x + e.x) / 2;
  const my = (s.y + e.y) / 2;
  const stroke = warning ? '#f59e0b' : missingCable ? '#ec4899' : (link.color || '#3b82f6');
  return (
    <g>
      {link.audioTransport && (
        <path
          d={`M ${s.x} ${s.y + 3} C ${s.x + dx} ${s.y + 3}, ${e.x - dx} ${e.y + 3}, ${e.x} ${e.y + 3}`}
          stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 5" opacity="0.9"
        />
      )}
      <path
        d={`M ${s.x} ${s.y} C ${s.x + dx} ${s.y}, ${e.x - dx} ${e.y}, ${e.x} ${e.y}`}
        stroke={stroke} strokeWidth="5" fill="none" strokeLinecap="round"
        strokeDasharray={missingCable ? '12 8' : undefined}
        className="opacity-80 drop-shadow-lg"
        onClick={event => onClick?.(event, link)}
        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
      />
      <path
        d={`M ${s.x} ${s.y} C ${s.x + dx} ${s.y}, ${e.x - dx} ${e.y}, ${e.x} ${e.y}`}
        stroke="transparent" strokeWidth="22" fill="none" strokeLinecap="round"
        onClick={event => onClick?.(event, link)}
        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
      />
      {warning && (
        <>
          <circle cx={mx} cy={my} r={11} fill="#78350f" stroke="#f59e0b" strokeWidth={1.5} opacity={0.95} />
          <text x={mx} y={my + 4.5} textAnchor="middle" fontSize={13} fill="#fcd34d" fontWeight="bold" fontFamily="sans-serif">!</text>
          <title>{warning}</title>
        </>
      )}
      {!warning && missingCable && <title>Brak przypisanego kabla — dodano do listy potrzeb</title>}
    </g>
  );
});

// ─── MemoNode ─────────────────────────────────────────────────────────────────
const displayPorts = (n, links) => {
  if (n.portsExpanded) return { inputs: n.inputs || [], outputs: n.outputs || [] };
  const usedIn = new Set(links.filter(link => link.toNode === n.instanceId).map(link => link.toPort));
  const usedOut = new Set(links.filter(link => link.fromNode === n.instanceId).map(link => link.fromPort));
  const inputs = (n.inputs || []).filter(port => usedIn.has(port.id));
  const outputs = (n.outputs || []).filter(port => usedOut.has(port.id));
  return {
    inputs: inputs.length ? inputs : (n.inputs || []).slice(0, 2),
    outputs: outputs.length ? outputs : (n.outputs || []).slice(0, 2),
  };
};

const MemoNode = memo(({ n, isDragging, onMouseDown, onPortMouseDown, onPortContext, onNodeContext, onTogglePorts, links, language }) => {
  const outgoingLink = links.find(l => l.fromNode === n.instanceId);
  const headerColor = n.color || outgoingLink?.color || null;
  const visible = displayPorts(n, links);
  return (
    <div
      className="absolute w-56 bg-[#161b26] border border-white/10 rounded-[30px] shadow-2xl touch-none overflow-hidden"
      style={{ left: n.x, top: n.y, zIndex: isDragging ? 100 : 20, willChange: 'left, top' }}
      onContextMenu={(e) => onNodeContext(e, n)}
    >
      <div
        className="h-14 border-b border-white/5 flex items-center justify-between px-5 cursor-grab active:cursor-grabbing transition-colors duration-200"
        style={{ backgroundColor: headerColor ? `${headerColor}33` : 'rgba(255,255,255,0.05)' }}
        onMouseDown={(e) => onMouseDown(e, n)}
      >
        <div className="flex items-center gap-3">
          {n.category === 'video'
            ? <Camera size={14} className={headerColor ? 'text-white' : 'text-indigo-400'} />
            : <Mic2 size={14} className={headerColor ? 'text-white' : 'text-green-400'} />}
          <span className="text-[11px] font-black uppercase truncate w-32 tracking-tight text-white">{n.model}</span>
        </div>
        <div className="flex items-center gap-2"><button onClick={(e) => { e.stopPropagation(); onTogglePorts(n.instanceId); }} className="text-[8px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-300">{n.portsExpanded ? dictionary(language).collapse : dictionary(language).expand}</button></div>
      </div>
      <div className="p-4 flex flex-col gap-0">
        {[...Array(Math.max(visible.inputs.length, visible.outputs.length))].map((_, idx) => (
          <div key={idx} className="h-10 flex justify-between items-center relative">
            {visible.inputs[idx] && (
              <div className="flex items-center gap-3">
                <div
                  className="port-dot w-7 h-7 rounded-full border-2 border-[#161b26] flex items-center justify-center transition-all cursor-crosshair z-30 shadow-md"
                  style={{ backgroundColor: links.find(l => l.toNode === n.instanceId && l.toPort === visible.inputs[idx].id)?.color || '#334155' }}
                  data-nid={n.instanceId} data-pid={visible.inputs[idx].id} data-ptype="in"
                  onMouseDown={(e) => onPortMouseDown(n, visible.inputs[idx], 'in', e)}
                  onContextMenu={(e) => onPortContext(e, n, visible.inputs[idx], 'in')}
                ><div className="w-1.5 h-1.5 bg-white/20 rounded-full" /></div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-slate-400 uppercase leading-none">{visible.inputs[idx].name}</span>
                  <span className="text-[7px] text-slate-600 font-black mt-0.5 uppercase italic">{visible.inputs[idx].type}</span>
                </div>
              </div>
            )}
            {visible.outputs[idx] && (
              <div className="flex items-center gap-3 ml-auto">
                <div className="flex flex-col text-right">
                    <span className="text-[9px] font-bold text-slate-300 uppercase leading-none">{visible.outputs[idx].name}</span>
                    <span className="text-[7px] text-indigo-700 font-black mt-0.5 uppercase italic">{visible.outputs[idx].type}</span>
                </div>
                <div
                  className="port-dot w-7 h-7 rounded-full border-2 border-[#161b26] flex items-center justify-center transition-all cursor-crosshair z-30 shadow-md"
                  style={{ backgroundColor: links.find(l => l.fromNode === n.instanceId && l.fromPort === visible.outputs[idx].id)?.color || '#334155' }}
                  data-nid={n.instanceId} data-pid={visible.outputs[idx].id} data-ptype="out"
                  onMouseDown={(e) => onPortMouseDown(n, visible.outputs[idx], 'out', e)}
                  onContextMenu={(e) => onPortContext(e, n, visible.outputs[idx], 'out')}
                ><div className="w-1.5 h-1.5 bg-white/20 rounded-full" /></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

// ─── InventoryView ────────────────────────────────────────────────────────────
// ─── ProjectModal ─────────────────────────────────────────────────────────────
const EventManagerModal = ({ projects, currentProjectId, onSelect, onCreate, onClose, language }) => {
  const t = dictionary(language);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [creating, setCreating] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      await onCreate({ name: name.trim(), date, location: location.trim(), formatId: '1080p50' });
      onClose();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center bg-black/70 backdrop-blur-sm p-5" onClick={onClose}>
      <div className="bg-[#12161f] border border-white/15 rounded-[40px] shadow-2xl w-[760px] max-w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={event => event.stopPropagation()}>
        <div className="flex items-center justify-between p-7 border-b border-white/10">
          <div><p className="text-[9px] font-black uppercase tracking-[0.2em] text-indigo-400">{t.production}</p><h2 className="text-lg font-black text-white mt-1">{t.eventsProjects}</h2></div>
          <button onClick={onClose} className="text-slate-600 hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid md:grid-cols-[1fr_290px] min-h-0">
          <div className="p-6 overflow-y-auto border-r border-white/10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-3">{t.yourEvents}</p>
            <div className="space-y-2">
              {projects.map(item => (
                <button key={item.id} onClick={() => { onSelect(item.id); onClose(); }} className={`w-full text-left p-4 rounded-2xl border transition-all ${item.id === currentProjectId ? 'border-indigo-500 bg-indigo-950/40' : 'border-white/5 bg-[#1a1f2b] hover:border-white/20'}`}>
                  <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="text-sm font-black text-white truncate">{item.name || t.unnamedProject}</p><div className="flex gap-3 mt-2 text-[9px] text-slate-500">{item.date && <span className="flex items-center gap-1"><CalendarDays size={10}/>{item.date}</span>}{item.location && <span className="flex items-center gap-1 truncate"><MapPin size={10}/>{item.location}</span>}</div></div>{item.id === currentProjectId && <span className="text-[8px] font-black uppercase tracking-widest text-indigo-300">{t.active}</span>}</div>
                </button>
              ))}
            </div>
          </div>
          <form onSubmit={submit} className="p-6 bg-[#0f1219]">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-4">{t.newEvent}</p>
            <label className="block text-[9px] font-bold text-slate-500 mb-1.5">{t.name} *</label>
            <input value={name} onChange={event => setName(event.target.value)} placeholder="np. Konferencja Warszawa" className="w-full bg-[#161b26] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-indigo-500 mb-4" />
            <label className="block text-[9px] font-bold text-slate-500 mb-1.5">{t.date}</label>
            <input type="date" value={date} onChange={event => setDate(event.target.value)} className="w-full bg-[#161b26] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-slate-300 outline-none focus:border-indigo-500 mb-4" />
            <label className="block text-[9px] font-bold text-slate-500 mb-1.5">{t.location}</label>
            <input value={location} onChange={event => setLocation(event.target.value)} placeholder="Miasto / obiekt" className="w-full bg-[#161b26] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-indigo-500 mb-5" />
            <button disabled={creating || !name.trim()} className="w-full bg-indigo-600 disabled:opacity-40 hover:bg-indigo-500 text-white rounded-xl py-3 text-[10px] font-black uppercase tracking-widest">{creating ? t.creating : `+ ${t.createEvent}`}</button>
          </form>
        </div>
      </div>
    </div>
  );
};

const ProjectModal = memo(({ project, onSave, onClose, language }) => {
  const t = dictionary(language);
  const [selected, setSelected] = useState(project.formatId);
  const groups = [
    { label: '720p — HD',       ids: ['720p25','720p50','720p60'] },
    { label: '1080 — Full HD',  ids: ['1080i50','1080i60','1080p25','1080p30','1080p50','1080p60'] },
    { label: 'UHD 4K',          ids: ['uhd4k25','uhd4k30','uhd4k50','uhd4k60'] },
    { label: '4K DCI — kino',   ids: ['dci4k24','dci4k25','dci4k60'] },
  ];
  const fmt = PROJECT_FORMATS.find(f => f.id === selected);
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#12161f] border border-white/15 rounded-[40px] shadow-2xl w-[520px] max-w-full p-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-sm font-black uppercase tracking-[0.2em] text-white">{t.projectSettings}</h2>
          <button onClick={onClose} className="text-slate-600 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        {/* Format aktualny */}
        {fmt && (
          <div className="mb-6 p-4 bg-indigo-950/40 border border-indigo-800/40 rounded-2xl">
            <p className="text-[9px] font-black uppercase tracking-widest text-indigo-400 mb-1">{t.selectedFormat}</p>
            <p className="text-xl font-black text-white">{fmt.label}</p>
            <p className="text-[11px] text-slate-400 mt-1">{fmt.res} · {fmt.fps} fps</p>
            <p className="text-[10px] text-indigo-400 mt-1 font-bold">{fmt.note}</p>
          </div>
        )}

        {/* Grupy formatów */}
        <div className="space-y-4 max-h-[340px] overflow-y-auto pr-1">
          {groups.map(g => (
            <div key={g.label}>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-2">{g.label}</p>
              <div className="grid grid-cols-2 gap-2">
                {g.ids.map(id => {
                  const f = PROJECT_FORMATS.find(x => x.id === id);
                  if (!f) return null;
                  const active = selected === id;
                  return (
                    <button key={id} onClick={() => setSelected(id)}
                      className={`text-left p-3 rounded-2xl border transition-all ${
                        active ? 'border-indigo-500 bg-indigo-950/60 text-white' : 'border-white/5 bg-[#1a1f2b] text-slate-400 hover:border-white/20'
                      }`}>
                      <p className={`text-[11px] font-black ${active ? 'text-indigo-300' : ''}`}>{f.label}</p>
                      <p className="text-[9px] text-slate-600 mt-0.5">{f.note}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Tabela limitów sygnału */}
        <div className="mt-5 p-4 bg-[#0b0e14] rounded-2xl border border-white/5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-3">{t.cableLimits}: <span className="text-indigo-400">{fmt?.label}</span></p>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(PORT_MAX_FORMAT).map(([type, maxFmt]) => {
              const rank = FORMAT_RANK[maxFmt] ?? 0;
              const prjRank = FORMAT_RANK[selected] ?? 0;
              const ok = rank >= prjRank;
              return (
                <div key={type} className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[9px] font-bold ${ok ? 'bg-green-950/30 text-green-400' : 'bg-red-950/30 text-red-400'}`}>
                  <span className="uppercase tracking-wide">{type}</span>
                  <span className={`text-[8px] ${ok ? 'text-green-600' : 'text-red-600'}`}>{ok ? '✓' : `max ${maxFmt}`}</span>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { onSave({ ...project, formatId: selected }); onClose(); }}
          className="mt-5 w-full bg-indigo-600 hover:bg-indigo-500 text-white font-black py-3.5 rounded-2xl text-[11px] uppercase tracking-widest transition-all">
          {t.saveProjectSettings}
        </button>
      </div>
    </div>
  );
});

// ─── SchemaExportView — ukryty widok do html2canvas ─────────────────────────
const EXPORT_W = 1680;
const EXPORT_H = 945;
const EXP_NODE_W = 200;
const EXP_NODE_HEAD = 52;
const EXP_NODE_PAD = 14;
const EXP_PORT_H = 36;
const expNodeH = (n) =>
  EXP_NODE_HEAD + EXP_NODE_PAD + Math.max(n.inputs?.length || 0, n.outputs?.length || 0) * EXP_PORT_H;

const EXP_CAT_COLORS = {
  video:        { border: '#3b82f6', headerBg: '#172554', bodyBg: '#161b26', accent: '#3b82f6', brandColor: '#93c5fd', typeColor: '#1d4ed8' },
  audio:        { border: '#10b981', headerBg: '#064e3b', bodyBg: '#161b26', accent: '#10b981', brandColor: '#6ee7b7', typeColor: '#065f46' },
  'network-av': { border: '#8b5cf6', headerBg: '#2e1065', bodyBg: '#161b26', accent: '#8b5cf6', brandColor: '#c4b5fd', typeColor: '#4c1d95' },
  display:      { border: '#f97316', headerBg: '#431407', bodyBg: '#161b26', accent: '#f97316', brandColor: '#fdba74', typeColor: '#9a3412' },
};

const SchemaExportView = ({ nodes, links, linkWarnings, containerRef }) => {
  // Bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  if (nodes.length > 0) {
    nodes.forEach(n => {
      const nh = expNodeH(n);
      minX = Math.min(minX, n.x); minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + EXP_NODE_W); maxY = Math.max(maxY, n.y + nh);
    });
  } else { minX = 0; minY = 0; maxX = 800; maxY = 400; }

  const PAD = 50;
  minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;

  const TITLE_H = 56, FOOTER_H = 28;
  const availW = EXPORT_W - 80;
  const availH = EXPORT_H - TITLE_H - FOOTER_H - 40;
  const scale = Math.min(availW / (maxX - minX), availH / (maxY - minY), 1.4);
  const contentW = (maxX - minX) * scale;
  const contentH = (maxY - minY) * scale;
  const ox = (EXPORT_W - contentW) / 2 - minX * scale;
  const oy = TITLE_H + 20 + (availH - contentH) / 2 - minY * scale;

  const tx = (x) => ox + x * scale;
  const ty = (y) => oy + y * scale;

  // Port center (absolute px within the EXPORT_W×EXPORT_H div)
  const portPos = (n, idx, isInput) => ({
    x: isInput ? tx(n.x) : tx(n.x + EXP_NODE_W),
    y: ty(n.y) + (EXP_NODE_HEAD + EXP_NODE_PAD / 2 + idx * EXP_PORT_H + EXP_PORT_H / 2) * scale,
  });

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        left: -(EXPORT_W + 300),
        top: 0,
        width: EXPORT_W,
        height: EXPORT_H,
        background: '#0b0e14',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        zIndex: 0,
      }}
    >
      {/* Dot grid */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#1e222d 1.5px, transparent 1.5px)', backgroundSize: '32px 32px', opacity: 0.35, pointerEvents: 'none' }} />

      {/* Title bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: TITLE_H, background: 'rgba(18,22,31,0.97)', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', padding: '0 32px', gap: 10, zIndex: 10 }}>
        <span style={{ fontSize: 20, fontWeight: 900, color: '#e2e8f0', letterSpacing: '-0.02em', fontStyle: 'italic', textTransform: 'uppercase' }}>GearNode</span>
        <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, letterSpacing: '0.2em' }}>BETA</span>
        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 10px' }} />
        <span style={{ fontSize: 12, color: '#64748b', letterSpacing: '0.05em', fontWeight: 600 }}>Schemat AV</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 20, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#334155', fontWeight: 600 }}>{nodes.length} urządzeń · {links.length} połączeń</span>
          <span style={{ fontSize: 10, color: '#1e3a5f', fontWeight: 600 }}>{new Date().toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* SVG — connections */}
      <svg style={{ position: 'absolute', inset: 0, width: EXPORT_W, height: EXPORT_H, overflow: 'visible', pointerEvents: 'none', zIndex: 5 }}>
        {links.map(l => {
          const fromN = nodes.find(n => n.instanceId === l.fromNode);
          const toN   = nodes.find(n => n.instanceId === l.toNode);
          if (!fromN || !toN) return null;
          const fIdx = fromN.outputs.findIndex(p => p.id === l.fromPort);
          const tIdx = toN.inputs.findIndex(p => p.id === l.toPort);
          if (fIdx < 0 || tIdx < 0) return null;
          const s = portPos(fromN, fIdx, false);
          const e = portPos(toN, tIdx, true);
          const dx = Math.abs(e.x - s.x) * 0.5;
          const mx = (s.x + e.x) / 2, my = (s.y + e.y) / 2;
          const isWarn = !!linkWarnings[l.id];
          const stroke = isWarn ? '#f59e0b' : (l.color || '#3b82f6');
          const sw = Math.max(2.5, 3.5 * scale);
          return (
            <g key={l.id}>
              {/* glow */}
              <path d={`M ${s.x} ${s.y} C ${s.x+dx} ${s.y}, ${e.x-dx} ${e.y}, ${e.x} ${e.y}`} stroke={stroke} strokeWidth={sw + 6} fill="none" strokeLinecap="round" opacity={0.15} />
              {/* cable */}
              <path d={`M ${s.x} ${s.y} C ${s.x+dx} ${s.y}, ${e.x-dx} ${e.y}, ${e.x} ${e.y}`} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="round" opacity={0.9} />
              {isWarn && (
                <>
                  <circle cx={mx} cy={my} r={12} fill="#78350f" stroke="#f59e0b" strokeWidth={2} />
                  <text x={mx} y={my + 4.5} textAnchor="middle" fontSize={13} fill="#fcd34d" fontWeight="bold" fontFamily="sans-serif">!</text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* Nodes */}
      {nodes.map(n => {
        const x = tx(n.x), y = ty(n.y);
        const w = EXP_NODE_W * scale, h = expNodeH(n) * scale;
        const r = Math.max(8, 14 * scale);
        const colors = EXP_CAT_COLORS[n.category] || EXP_CAT_COLORS.video;
        return (
          <div key={n.instanceId} style={{ position: 'absolute', left: x, top: y, width: w, height: h, zIndex: 6 }}>
            {/* Card */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: r,
              border: `1px solid ${colors.border}55`,
              boxShadow: `0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px ${colors.border}18`,
              overflow: 'hidden',
            }}>
              {/* Accent stripe */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: Math.max(2, 2 * scale), background: colors.accent, zIndex: 1 }} />
              {/* Header */}
              <div style={{
                height: EXP_NODE_HEAD * scale,
                background: `linear-gradient(135deg, ${colors.headerBg} 0%, rgba(22,27,38,0.9) 100%)`,
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                padding: `0 ${Math.max(8, 10 * scale)}px`,
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                position: 'relative',
              }}>
                {n.imageUrl && (
                  <img src={n.imageUrl} alt="" crossOrigin="anonymous" style={{
                    position: 'absolute', right: Math.max(6, 8 * scale), top: '50%', transform: 'translateY(-50%)',
                    width: Math.max(24, 30 * scale), height: Math.max(24, 30 * scale), objectFit: 'contain',
                  }} />
                )}
                <div style={{ fontSize: Math.max(7, 7.5 * scale), color: colors.brandColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1 }}>{n.brand}</div>
                <div style={{
                  fontSize: Math.max(9, 10.5 * scale), color: '#f1f5f9', fontWeight: 900,
                  marginTop: Math.max(1, 2 * scale), lineHeight: 1.2,
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  maxWidth: n.imageUrl ? `${w * 0.68}px` : '95%',
                }}>{n.model}</div>
              </div>
              {/* Ports body */}
              <div style={{ background: colors.bodyBg, height: h - EXP_NODE_HEAD * scale, padding: `${EXP_NODE_PAD * scale / 2}px 0` }}>
                {Array.from({ length: Math.max(n.inputs.length, n.outputs.length) }).map((_, idx) => (
                  <div key={idx} style={{ height: EXP_PORT_H * scale, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {n.inputs[idx] && (
                      <div style={{ paddingLeft: Math.max(8, 10 * scale) }}>
                        <div style={{ fontSize: Math.max(7, 7.5 * scale), color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>{n.inputs[idx].name}</div>
                        <div style={{ fontSize: Math.max(5.5, 6 * scale), color: '#475569', fontWeight: 900, textTransform: 'uppercase', marginTop: Math.max(1, 1.5 * scale) }}>{n.inputs[idx].type}</div>
                      </div>
                    )}
                    {n.outputs[idx] && (
                      <div style={{ paddingRight: Math.max(8, 10 * scale), marginLeft: 'auto', textAlign: 'right' }}>
                        <div style={{ fontSize: Math.max(7, 7.5 * scale), color: '#cbd5e1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>{n.outputs[idx].name}</div>
                        <div style={{ fontSize: Math.max(5.5, 6 * scale), color: '#4338ca', fontWeight: 900, textTransform: 'uppercase', marginTop: Math.max(1, 1.5 * scale) }}>{n.outputs[idx].type}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Port dots — outside clip, aligned to SVG */}
            {n.inputs.map((p, idx) => {
              const dotAbsY = portPos(n, idx, true).y;
              const dotY = dotAbsY - y;
              const dotSize = Math.max(8, 10 * scale);
              return (
                <div key={p.id} style={{
                  position: 'absolute', left: -dotSize / 2, top: dotY - dotSize / 2,
                  width: dotSize, height: dotSize, borderRadius: '50%',
                  background: colors.accent, border: `${Math.max(1.5, 2 * scale)}px solid ${colors.bodyBg}`,
                  boxShadow: `0 0 8px ${colors.accent}70`, zIndex: 7,
                }} />
              );
            })}
            {n.outputs.map((p, idx) => {
              const dotAbsY = portPos(n, idx, false).y;
              const dotY = dotAbsY - y;
              const dotSize = Math.max(8, 10 * scale);
              return (
                <div key={p.id} style={{
                  position: 'absolute', right: -dotSize / 2, top: dotY - dotSize / 2,
                  width: dotSize, height: dotSize, borderRadius: '50%',
                  background: colors.accent, border: `${Math.max(1.5, 2 * scale)}px solid ${colors.bodyBg}`,
                  boxShadow: `0 0 8px ${colors.accent}70`, zIndex: 7,
                }} />
              );
            })}
          </div>
        );
      })}

      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: FOOTER_H,
        borderTop: '1px solid rgba(255,255,255,0.04)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, color: '#1e293b', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase',
      }}>
        GearNode BETA · Schemat połączeń AV
      </div>
    </div>
  );
};

// ─── InventoryView ────────────────────────────────────────────────────────────
// Legacy view kept temporarily for migration reference while the new warehouse is validated.
// eslint-disable-next-line no-unused-vars
const InventoryView = memo(({ inventory, assignedAssetIds, assignedAnywhere, conflicts, onUpdateInventory, onToggleAsset }) => {
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  const filteredCatalog = useMemo(() => {
    const q = search.toLowerCase();
    return DEVICE_CATALOG.filter(d => {
      const matchCat = activeCat === 'all' || d.category === activeCat;
      const matchQ = !q || d.brand.toLowerCase().includes(q) || d.model.toLowerCase().includes(q) || d.type.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [search, activeCat]);

  const inventoryItems = useMemo(() =>
    inventory.map(item => ({ ...item, device: DEVICE_CATALOG.find(d => d.id === item.deviceId) }))
             .filter(item => item.device),
    [inventory]
  );

  const totalPower = useMemo(() =>
    inventoryItems.reduce((sum, item) => sum + (item.device.power || 0), 0),
    [inventoryItems]
  );

  const isOwned = (id) => inventory.some(i => i.deviceId === id);

  const addDevice = (deviceId) => {
    onUpdateInventory([...inventory, createAsset(deviceId, inventory.length)]);
  };

  const removeDevice = (assetId) => {
    if (assignedAnywhere.has(assetId)) {
      alert('Najpierw odłącz ten egzemplarz od wszystkich eventów.');
      return;
    }
    onUpdateInventory(inventory.filter(item => item.assetId !== assetId));
  };

  const updateNotes = (assetId, notes) => {
    onUpdateInventory(inventory.map(item => item.assetId === assetId ? { ...item, notes } : item));
  };

  const updateSerial = (assetId, serialNumber) => {
    onUpdateInventory(inventory.map(item => item.assetId === assetId ? { ...item, serialNumber } : item));
  };

  const updatePurchaseDate = (assetId, purchaseDate) => {
    onUpdateInventory(inventory.map(item => item.assetId === assetId ? { ...item, purchaseDate } : item));
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(inventory, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gearnode-sprzet-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if (Array.isArray(parsed)) onUpdateInventory(parsed);
      } catch { alert('Błąd: nieprawidłowy plik JSON'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="flex-1 flex overflow-hidden">

      {/* ── LEWY PANEL: Katalog ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden border-r border-white/10">

        {/* Wyszukiwarka + filtry */}
        <div className="p-4 bg-[#0f1219] border-b border-white/10 space-y-3 shrink-0">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Szukaj po nazwie, marce, typie..."
              className="w-full bg-[#1a1f2b] border border-white/10 rounded-2xl pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 transition-colors"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setActiveCat(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeCat === cat.id ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' : 'bg-white/5 text-slate-400 hover:bg-white/10'
                }`}>
                {cat.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-600">{filteredCatalog.length} urządzeń w katalogu</p>
        </div>

        {/* Siatka urządzeń */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredCatalog.map(device => {
              const owned = isOwned(device.id);
              return (
                <div key={device.id}
                  className={`flex flex-col gap-3 p-4 rounded-2xl border transition-all ${
                    owned ? 'border-indigo-500/40 bg-indigo-950/20' : 'border-white/5 bg-[#1a1f2b] hover:border-white/20'
                  }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest leading-none">{device.brand}</p>
                      <p className="text-[13px] font-black text-white leading-tight mt-1 truncate">{device.model}</p>
                      <p className="text-[10px] text-slate-500 mt-1 truncate">{device.type}</p>
                    </div>
                    <span className={`shrink-0 text-[8px] font-black uppercase tracking-wider px-2 py-1 rounded-lg ${CAT_BADGE[device.category] || 'bg-slate-800 text-slate-400'}`}>
                      {device.category}
                    </span>
                  </div>
                  {device.power > 0 && (
                    <p className="text-[9px] text-yellow-700 font-bold">{device.power}W</p>
                  )}
                  <button
                    onClick={() => addDevice(device.id)}
                    className={`w-full py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      owned
                        ? 'bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/35'
                        : 'bg-white/5 text-slate-400 hover:bg-indigo-600 hover:text-white'
                    }`}>
                    {owned ? '+ Dodaj kolejną sztukę' : '+ Dodaj do sprzętu'}
                  </button>
                </div>
              );
            })}
          </div>
          {filteredCatalog.length === 0 && (
            <div className="text-center py-20 text-slate-600">
              <p className="text-5xl mb-4">🔍</p>
              <p className="font-black uppercase tracking-widest text-sm">Brak wyników</p>
            </div>
          )}
        </div>
      </div>

      {/* ── PRAWY PANEL: Mój sprzęt ─────────────────────────────────── */}
      <div className="w-[360px] shrink-0 flex flex-col bg-[#0b0e14] overflow-hidden">

        {/* Statystyki */}
        <div className="p-4 border-b border-white/10 bg-[#0f1219] shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-black uppercase tracking-widest text-white flex items-center gap-2">
              <Package size={13} className="text-indigo-400" /> Mój sprzęt
            </h2>
            <span className="text-[10px] text-slate-500 bg-white/5 px-2 py-1 rounded-lg font-bold">{inventoryItems.length} szt.</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#1a1f2b] rounded-xl px-3 py-2">
              <p className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Urządzeń</p>
              <p className="text-lg font-black text-white mt-0.5">{inventoryItems.length}</p>
            </div>
            <div className="bg-[#1a1f2b] rounded-xl px-3 py-2">
              <p className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Moc łączna</p>
              <p className="text-lg font-black text-yellow-400 mt-0.5">{totalPower}W</p>
            </div>
          </div>
        </div>

        {/* Lista sprzętu */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {inventoryItems.length === 0 && (
            <div className="text-center py-20 text-slate-600">
              <p className="text-5xl mb-4">📦</p>
              <p className="font-black uppercase tracking-widest text-sm">Pusta lista</p>
              <p className="text-xs mt-2 text-slate-700">Dodaj urządzenia z katalogu</p>
            </div>
          )}
          {inventoryItems.map(({ assetId, notes, serialNumber, purchaseDate, device }) => {
            const assigned = assignedAssetIds.includes(assetId);
            const conflict = conflicts.has(assetId);
            return (
            <div key={assetId} className={`bg-[#161b26] border rounded-2xl p-3 space-y-2 ${conflict ? 'border-red-500/50' : assigned ? 'border-indigo-500/40' : 'border-white/5'}`}>
              {/* Nagłówek karty */}
              <div className="flex items-start gap-2">
                {device.imageUrl && (
                  <img src={device.imageUrl} alt={device.model}
                    className="w-10 h-10 object-contain rounded-lg bg-[#0f1219] border border-white/5 shrink-0 p-1" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">{device.brand}</p>
                  <p className="text-[12px] font-black text-white truncate leading-tight mt-0.5">{device.model}</p>
                  {device.power > 0 && (
                    <p className="text-[9px] text-yellow-700 mt-1 font-bold">
                      {device.power}W
                    </p>
                  )}
                </div>
                <button onClick={() => removeDevice(assetId)} disabled={assignedAnywhere.has(assetId)} title={assignedAnywhere.has(assetId) ? 'Egzemplarz jest przypisany do eventu' : 'Usuń egzemplarz'}
                  className="shrink-0 text-slate-700 hover:text-red-400 disabled:opacity-30 disabled:hover:text-slate-700 transition-colors mt-0.5">
                  <X size={14} />
                </button>
              </div>
              {/* Nr seryjny */}
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-black uppercase text-slate-600 tracking-widest shrink-0 w-14">S/N</span>
                <input
                  type="text" value={serialNumber || ''}
                  onChange={e => updateSerial(assetId, e.target.value)}
                  placeholder="Nr seryjny..."
                  className="flex-1 bg-[#0f1219] border border-white/5 rounded-xl px-3 py-1.5 text-[10px] text-slate-300 placeholder:text-slate-700 outline-none focus:border-indigo-500/40 transition-colors font-mono"
                />
              </div>
              {/* Data zakupu */}
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-black uppercase text-slate-600 tracking-widest shrink-0 w-14">Zakup</span>
                <input
                  type="date" value={purchaseDate || ''}
                  onChange={e => updatePurchaseDate(assetId, e.target.value)}
                  className="flex-1 bg-[#0f1219] border border-white/5 rounded-xl px-3 py-1.5 text-[10px] text-slate-400 outline-none focus:border-indigo-500/40 transition-colors"
                />
              </div>
              {/* Notatka */}
              <input
                type="text" value={notes}
                onChange={e => updateNotes(assetId, e.target.value)}
                placeholder="Notatka (np. własna, wynajem)..."
                className="w-full bg-[#0f1219] border border-white/5 rounded-xl px-3 py-1.5 text-[10px] text-slate-400 placeholder:text-slate-700 outline-none focus:border-indigo-500/40 transition-colors"
              />
              <button onClick={() => onToggleAsset(assetId)} className={`w-full py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${assigned ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}>
                {conflict ? '⚠ Konflikt daty — przypisany' : assigned ? '✓ Przypisany do eventu' : '+ Przypisz do eventu'}
              </button>
            </div>
          );})}
        </div>

        {/* Eksport / Import */}
        <div className="p-3 border-t border-white/10 space-y-2 shrink-0">
          <button onClick={exportJSON}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-400 font-bold py-3 rounded-2xl text-[10px] uppercase tracking-widest transition-all">
            <Download size={13} /> Eksportuj JSON
          </button>
          <label className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-slate-400 font-bold py-3 rounded-2xl text-[10px] uppercase tracking-widest transition-all cursor-pointer">
            <Upload size={13} /> Importuj JSON
            <input type="file" accept=".json" className="hidden" onChange={importJSON} />
          </label>
        </div>
      </div>
    </div>
  );
});

const CableInventoryView = memo(({ cables, usedCableIds, onUpdate, language }) => {
  const t = dictionary(language);
  const [typeId, setTypeId] = useState('bnc');
  const updateCable = (cableId, changes) => onUpdate(cables.map(cable => cable.cableId === cableId ? { ...cable, ...changes } : cable));
  const removeCable = cableId => {
    if (usedCableIds.has(cableId)) return alert('Ten kabel jest przypisany do połączenia. Najpierw odłącz go na schemacie.');
    onUpdate(cables.filter(cable => cable.cableId !== cableId));
  };
  return (
    <div className="flex-1 overflow-auto bg-[#0b0e14] p-6 md:p-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[.25em] text-indigo-400">{t.globalWarehouse}</p>
            <h2 className="mt-2 text-3xl font-black text-white">{t.physicalCables}</h2>
            <p className="mt-2 text-sm text-slate-500">{t.cableDescription}</p>
          </div>
          <div className="flex gap-2">
            <select value={typeId} onChange={event => setTypeId(event.target.value)} className="rounded-2xl border border-white/10 bg-[#171c27] px-4 py-3 text-xs font-bold text-white">
              {CABLE_TYPES.map(type => <option key={type.id} value={type.id}>{type.label}</option>)}
            </select>
            <button onClick={() => onUpdate([...cables, createCable(typeId, cables.length)])} className="flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white"><Plus size={14}/> {t.addCable}</button>
          </div>
        </div>
        {cables.length === 0 ? <div className="rounded-[32px] border border-dashed border-white/10 p-16 text-center text-sm text-slate-600">{t.emptyCableWarehouse}</div> : (
          <div className="grid gap-4 md:grid-cols-2">
            {cables.map(cable => {
              const type = CABLE_TYPES.find(item => item.id === cable.typeId);
              const used = usedCableIds.has(cable.cableId);
              return <div key={cable.cableId} className="rounded-[28px] border border-white/10 bg-[#12161f] p-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div><p className="text-xs font-black text-white">{cable.label}</p><p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">{type?.label} · {used ? t.assigned : cable.status === 'service' ? t.service : t.available}</p></div>
                  <button onClick={() => removeCable(cable.cableId)} disabled={used} className="text-slate-600 hover:text-red-400 disabled:opacity-20"><X size={16}/></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input value={cable.label} onChange={e => updateCable(cable.cableId, { label: e.target.value })} placeholder={t.cableName} className="col-span-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white" />
                  <input type="number" min="0" step="0.5" value={cable.lengthM} onChange={e => updateCable(cable.cableId, { lengthM: e.target.value })} placeholder={t.length} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white" />
                  <select value={cable.status} onChange={e => updateCable(cable.cableId, { status: e.target.value })} disabled={used} className="rounded-xl border border-white/10 bg-[#171c27] px-3 py-2 text-xs text-white"><option value="available">{t.available}</option><option value="service">{t.service}</option></select>
                  <input value={cable.endA} onChange={e => updateCable(cable.cableId, { endA: e.target.value })} placeholder={t.endA} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white" />
                  <input value={cable.endB} onChange={e => updateCable(cable.cableId, { endB: e.target.value })} placeholder={t.endB} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white" />
                </div>
              </div>;
            })}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── GŁÓWNA APLIKACJA ─────────────────────────────────────────────────────────
const App = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('gearnode_language') || 'pl');
  const t = dictionary(language);
  const initialProjectId = useMemo(() => localStorage.getItem('gearnode_current_project') || DEFAULT_PROJECT_ID, []);
  const initialWorkspace = useMemo(() => loadLocalWorkspace(initialProjectId), [initialProjectId]);
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [projects, setProjects]       = useState(listLocalProjects);
  const [currentProjectId, setCurrentProjectId] = useState(initialProjectId);
  const [openProjectIds, setOpenProjectIds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gearnode_open_projects'));
      return Array.isArray(saved) && saved.length ? saved.slice(0, 5) : [initialProjectId];
    } catch { return [initialProjectId]; }
  });
  const [showEventManager, setShowEventManager] = useState(false);
  const [appView, setAppView]         = useState('editor'); // 'editor' | 'inventory' | 'cables'
  const requestedAssetCode = useMemo(() => new URLSearchParams(window.location.search).get('asset') || '', []);
  const [inventory, setInventory]     = useState(() => loadInventory(initialWorkspace.inventory || []));
  const [customCatalog, setCustomCatalog] = useState(loadCustomCatalog);
  const [cableInventory, setCableInventory] = useState(loadCableInventory);
  const [assignedAssetIds, setAssignedAssetIds] = useState(() => initialWorkspace.assignedAssetIds || []);
  const [nodes, setNodes]             = useState(() => initialWorkspace.nodes);
  const [links, setLinks]             = useState(() => initialWorkspace.links);
  const [history, setHistory]         = useState([]);
  const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [interactionMode, setInteractionMode] = useState('select');
  const [draggingNode, setDraggingNode]   = useState(null);
  const [activeWire, setActiveWire]       = useState(null);
  const [project, setProject]             = useState(() => initialWorkspace.project);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [syncError, setSyncError]         = useState('');
  const projectRef = useRef({ formatId: '1080p50' });
  useEffect(() => { projectRef.current = project; }, [project]);
  const [isPanning, setIsPanning]         = useState(false);
  const [panStart, setPanStart]           = useState({ x: 0, y: 0 });
  const [portMenu, setPortMenu]           = useState({ visible: false, x: 0, y: 0, nodeId: null, portId: null, type: null });
  const [nodeMenu, setNodeMenu]           = useState({ visible: false, x: 0, y: 0, nodeId: null });
  const [connError, setConnError]         = useState({ visible: false, msg: '', x: 0, y: 0 });
  const connErrorTimer = useRef(null);

  const canvasRef         = useRef(null);
  const requestRef        = useRef();
  const nodesRef          = useRef([]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  const linksRef          = useRef([]);
  useEffect(() => { linksRef.current = links; }, [links]);
  const schemaExportRef   = useRef(null);

  const saveToCloud = useCallback(async (newNodes, newLinks, overrides = {}) => {
    const workspaceNodes = newNodes ?? nodesRef.current;
    const automaticAssetIds = [...new Set(workspaceNodes.map(node => node.assetId).filter(Boolean))];
    const workspace = {
      nodes: workspaceNodes,
      links: newLinks ?? linksRef.current,
      assignedAssetIds: overrides.assignedAssetIds ?? automaticAssetIds,
      project: overrides.project ?? projectRef.current,
    };
    try {
      await saveWorkspace(user, workspace, currentProjectId);
      if (!user || user.isAnonymous) setProjects(listLocalProjects());
      setSyncError('');
    } catch (error) {
      console.error('Workspace save failed:', error);
      setSyncError('Nie udało się zsynchronizować projektu. Zmiany zachowano lokalnie.');
    }
  }, [user, currentProjectId]);

  useEffect(() => { document.title = "GearNode BETA"; }, []);
  useEffect(() => {
    localStorage.setItem('gearnode_language', language);
    document.documentElement.lang = language;
  }, [language]);
  useEffect(() => {
    if (requestedAssetCode) setAppView('inventory');
  }, [requestedAssetCode]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setUser(u); setLoading(false); });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const localWorkspace = loadLocalWorkspace(currentProjectId);
    setNodes(localWorkspace.nodes);
    setLinks(localWorkspace.links);
    setAssignedAssetIds(localWorkspace.assignedAssetIds || []);
    setProject(localWorkspace.project);
    setHistory([]);
    if (user.isAnonymous) return;
    const unsub = subscribeWorkspace(user, workspace => {
      setNodes(workspace.nodes || []);
      setLinks(workspace.links || []);
      setAssignedAssetIds(workspace.assignedAssetIds || []);
      if (workspace.project) setProject(workspace.project);
      setSyncError('');
    }, error => {
      console.error('Workspace sync failed:', error);
      setSyncError('Brak dostępu do synchronizacji chmurowej. Pracujesz na kopii lokalnej.');
    }, currentProjectId);
    return () => unsub();
  }, [user, currentProjectId]);

  useEffect(() => {
    if (!user) return;
    return subscribeProjectList(user, setProjects, error => {
      console.error('Project list sync failed:', error);
      setSyncError('Nie udało się pobrać listy eventów.');
    });
  }, [user]);

  useEffect(() => {
    if (!projects.length || projects.some(item => item.id === currentProjectId)) return;
    const fallbackId = projects[0].id;
    localStorage.setItem('gearnode_current_project', fallbackId);
    setCurrentProjectId(fallbackId);
  }, [projects, currentProjectId]);

  const selectProject = useCallback((projectId) => {
    localStorage.setItem('gearnode_current_project', projectId);
    setOpenProjectIds(current => {
      if (current.includes(projectId)) return current;
      const next = current.length < 5 ? [...current, projectId] : [...current.slice(1), projectId];
      localStorage.setItem('gearnode_open_projects', JSON.stringify(next));
      return next;
    });
    setCurrentProjectId(projectId);
  }, []);

  const closeProjectTab = useCallback((projectId) => {
    setOpenProjectIds(current => {
      if (current.length === 1) return current;
      const next = current.filter(id => id !== projectId);
      localStorage.setItem('gearnode_open_projects', JSON.stringify(next));
      if (projectId === currentProjectId) selectProject(next[next.length - 1]);
      return next;
    });
  }, [currentProjectId, selectProject]);

  const handleCreateProject = useCallback(async (projectData) => {
    const projectId = await createWorkspace(user, projectData);
    if (!user || user.isAnonymous) setProjects(listLocalProjects());
    selectProject(projectId);
  }, [user, selectProject]);

  // Zapis inventory do localStorage przy każdej zmianie
  const handleUpdateInventory = useCallback((newInv) => {
    setInventory(newInv);
    saveInventory(user, newInv).catch(error => {
      console.error('Inventory save failed:', error);
      setSyncError('Nie udało się zsynchronizować magazynu. Zmiany zachowano lokalnie.');
    });
  }, [user]);

  const combinedCatalog = useMemo(() => [...DEVICE_CATALOG, ...customCatalog], [customCatalog]);

  const addWarehouseDevice = useCallback((customForm, catalogDevice) => {
    let device = catalogDevice;
    if (customForm) {
      device = createCustomDevice(customForm);
      const nextCatalog = [...customCatalog, device];
      setCustomCatalog(nextCatalog);
      saveCustomCatalog(user, nextCatalog).catch(error => {
        console.error('Custom catalog save failed:', error);
        setSyncError('Nie udało się zsynchronizować własnych urządzeń.');
      });
      if (customForm.submitToCatalog) submitCustomDevice(user, device).catch(error => {
        console.error('Catalog submission failed:', error);
        setSyncError('Urządzenie zapisano prywatnie, ale zgłoszenie do katalogu nie zostało wysłane.');
      });
    }
    if (!device) return;
    const nextInventory = [...inventory, createAsset(device.id, inventory.length)];
    handleUpdateInventory(nextInventory);
  }, [customCatalog, handleUpdateInventory, inventory, user]);

  const handleUpdateCableInventory = useCallback((newCables) => {
    setCableInventory(newCables);
    saveCableInventory(user, newCables).catch(error => {
      console.error('Cable inventory save failed:', error);
      setSyncError('Nie udało się zsynchronizować magazynu kabli. Zmiany zachowano lokalnie.');
    });
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    return subscribeInventory(user, setInventory, error => {
      console.error('Inventory sync failed:', error);
      setSyncError('Nie udało się pobrać magazynu.');
    });
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    return subscribeCustomCatalog(user, setCustomCatalog, error => {
      console.error('Custom catalog sync failed:', error);
      setSyncError('Nie udało się pobrać własnych urządzeń.');
    });
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    return subscribeCableInventory(user, setCableInventory, error => {
      console.error('Cable inventory sync failed:', error);
      setSyncError('Nie udało się pobrać magazynu kabli.');
    });
  }, [user]);

  const usedCableIds = useMemo(() => new Set(links.map(link => link.cableId).filter(Boolean)), [links]);

  const assetConflicts = useMemo(
    () => findAssetConflicts(projects, currentProjectId, assignedAssetIds),
    [projects, currentProjectId, assignedAssetIds]
  );
  const assignedAnywhere = useMemo(
    () => new Set(projects.flatMap(item => item.assignedAssetIds || [])),
    [projects]
  );

  // Urządzenia dostępne w sidebarze edytora = tylko z inventory
  const sidebarDevices = useMemo(() =>
    inventory.filter(item => item.status === 'available' || !item.status)
             .map(item => ({ ...item, device: combinedCatalog.find(d => d.id === item.deviceId) }))
             .filter(i => i.device),
    [inventory, combinedCatalog]
  );

  // ─── Statystyki canvas (Faza 3) ──────────────────────────────────────────────
  const canvasStats = useMemo(() => {
    const power = nodes.reduce((s, n) => s + (n.power || 0), 0);
    const cables = summarizeCableNeeds(links, nodes);
    return { power, cables, nodeCount: nodes.length, linkCount: links.length };
  }, [nodes, links]);

  // Ostrzeżenia na kablach (przeliczane gdy zmienia się projekt/nodes/links)
  const linkWarnings = useMemo(() => {
    const map = {};
    links.forEach(l => {
      const fromN = nodes.find(n => n.instanceId === l.fromNode);
      const toN   = nodes.find(n => n.instanceId === l.toNode);
      if (!fromN || !toN) return;
      const fromP = fromN.outputs.find(p => p.id === l.fromPort);
      const toP   = toN.inputs.find(p => p.id === l.toPort);
      if (!fromP || !toP) return;
      const result = checkConnection(fromP.type, toP.type, project.formatId);
      const formatLimit = [fromN, toN].find(node => Array.isArray(node.supportedFormats) && node.supportedFormats.length && !node.supportedFormats.includes(project.formatId));
      if (result.status === 'warn') map[l.id] = result.message;
      else if (formatLimit) map[l.id] = `${formatLimit.brand} ${formatLimit.model} nie ma formatu ${project.formatId} na liście obsługiwanych`;
    });
    return map;
  }, [links, nodes, project]);

  // ─── Eksport PDF (Faza 4) ─────────────────────────────────────────────────────
  const [pdfExporting, setPdfExporting] = useState(false);

  const exportPDF = useCallback(async () => {
    if (nodes.length === 0) return;
    setPdfExporting(true);
    try {
      // Załaduj jsPDF
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      // Załaduj html2canvas
      if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const { jsPDF } = window.jspdf;
      const PW = 297, PH = 210, M = 8; // A4 landscape mm
      const CAT_RGB = { video:[59,130,246], audio:[16,185,129], 'network-av':[139,92,246], display:[249,115,22] };

      // ── STRONA 1: Screenshot schematu (html2canvas) ───────────────────────
      const el = schemaExportRef.current;
      // Przesuń na ekran tymczasowo (pod aplikacją, z-index -1)
      el.style.left = '0px';
      el.style.zIndex = '-1';
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const captureCanvas = await window.html2canvas(el, {
        scale: 1,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#0b0e14',
        width: EXPORT_W,
        height: EXPORT_H,
        logging: false,
      });

      // Cofnij poza ekran
      el.style.left = `${-(EXPORT_W + 300)}px`;
      el.style.zIndex = '';

      const imgData = captureCanvas.toDataURL('image/jpeg', 0.93);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFillColor(11, 14, 20);
      doc.rect(0, 0, PW, PH, 'F');
      const imgAspect = EXPORT_H / EXPORT_W;
      const imgW = PW - 2 * M;
      const imgH = imgW * imgAspect;
      const imgY = (PH - imgH) / 2;
      doc.addImage(imgData, 'JPEG', M, Math.max(M / 2, imgY), imgW, Math.min(PH - M, imgH));

      // ── STRONA 2: Rejestr sprzętu (inventory) ────────────────────────────
      doc.addPage('a4', 'portrait');
      const P2W = 210, P2H = 297, P2M = 15;
      const addPageBg = () => { doc.setFillColor(11, 14, 20); doc.rect(0, 0, P2W, P2H, 'F'); };
      addPageBg();

      const pdfDate = new Date().toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' });
      const totalItems = inventory.length;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(200, 210, 240);
      doc.text('Rejestr sprzętu', P2M, P2M);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(90, 100, 120);
      doc.text(pdfDate, P2W - P2M, P2M, { align: 'right' });
      doc.text(`${new Set(inventory.map(item => item.deviceId)).size} modeli · ${totalItems} szt. łącznie`, P2M, P2M + 6);

      // Projekt
      const projFmt = PROJECT_FORMATS.find(f => f.id === project.formatId);
      if (projFmt) {
        doc.setFontSize(7);
        doc.setTextColor(100, 115, 160);
        doc.text(`Format projektu: ${projFmt.label} — ${projFmt.note}`, P2M, P2M + 11);
      }

      let y2 = P2M + 18;

      // Kolumny: PRODUCENT/MODEL | S/N | DATA | MOC
      const C2 = { brand: P2M + 2, sn: P2M + 68, date: P2M + 120, power: P2W - P2M - 2 };

      const drawPageHeader = () => {
        doc.setFillColor(22, 32, 50);
        doc.rect(P2M, y2 - 5, P2W - 2*P2M, 8.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(80, 100, 135);
        doc.text('PRODUCENT / MODEL', C2.brand, y2);
        doc.text('NR SERYJNY', C2.sn, y2);
        doc.text('DATA ZAKUPU', C2.date, y2);
        doc.text('MOC', C2.power, y2, { align: 'right' });
        y2 += 6;
      };
      drawPageHeader();

      inventory.forEach((item, idx) => {
        const device = DEVICE_CATALOG.find(d => d.id === item.deviceId);
        if (!device) return;

        const rowH = item.notes ? 16 : 13;
        if (y2 > P2H - 30) {
          doc.addPage('a4', 'portrait');
          addPageBg();
          y2 = P2M + 10;
          drawPageHeader();
        }

        // Parzysty wiersz
        if (idx % 2 === 0) {
          doc.setFillColor(15, 20, 30);
          doc.rect(P2M, y2 - 5, P2W - 2*P2M, rowH, 'F');
        }

        // Kolorowy pasek kategorii
        const [cr2, cg2, cb2] = CAT_RGB[device.category] || [99,102,241];
        doc.setFillColor(cr2, cg2, cb2);
        doc.rect(P2M, y2 - 5, 2, rowH, 'F');

        // Producent
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(110, 125, 155);
        doc.text(device.brand, C2.brand, y2 - 1.5);

        // Model
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(215, 225, 245);
        doc.text(device.model, C2.brand, y2 + 3.5, { maxWidth: 64 });

        // S/N
        if (item.serialNumber) {
          doc.setFont('courier', 'bold');
          doc.setFontSize(7.5);
          doc.setTextColor(180, 200, 230);
          doc.text(item.serialNumber, C2.sn, y2 + 1.5, { maxWidth: 48 });
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(45, 55, 75);
          doc.text('—', C2.sn, y2 + 1.5);
        }

        // Data zakupu
        if (item.purchaseDate) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(160, 175, 205);
          doc.text(new Date(item.purchaseDate).toLocaleDateString('pl-PL'), C2.date, y2 + 1.5);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(45, 55, 75);
          doc.text('—', C2.date, y2 + 1.5);
        }

        // Moc
        if (device.power > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(205, 165, 40);
          doc.text(`${device.power}W`, C2.power, y2 + 1.5, { align: 'right' });
        }

        // Notatka
        if (item.notes) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(6.5);
          doc.setTextColor(75, 92, 120);
          doc.text(item.notes, C2.brand, y2 + 9, { maxWidth: P2W - 2*P2M - 4 });
        }

        y2 += rowH;
      });

      // Suma mocy
      y2 += 4;
      doc.setDrawColor(35, 50, 78);
      doc.setLineWidth(0.3);
      doc.line(P2M, y2, P2W - P2M, y2);
      y2 += 5;
      const totalInvPower = inventory.reduce((s, item) => {
        const d = DEVICE_CATALOG.find(x => x.id === item.deviceId);
        return s + (d?.power || 0);
      }, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(205, 165, 40);
      doc.text(`Łączna moc: ${totalInvPower}W`, C2.power, y2, { align: 'right' });

      // ── STRONA 3: Kable ze schematu ──────────────────────────────────────
      doc.addPage('a4', 'portrait');
      addPageBg();
      let y3 = P2M;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(200, 210, 240);
      doc.text('Lista kabli — schemat', P2M, y3);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(90, 100, 120);
      doc.text(pdfDate, P2W - P2M, y3, { align: 'right' });
      y3 += 12;

      const cableList = Object.entries(canvasStats.cables);
      if (cableList.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(60, 75, 100);
        doc.text('Brak połączeń na schemacie.', P2M, y3);
      } else {
        // Header
        doc.setFillColor(22, 32, 50);
        doc.rect(P2M, y3 - 5, P2W - 2*P2M, 8.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(80, 100, 135);
        doc.text('TYP ZŁĄCZA / KABLA', P2M+2, y3);
        doc.text('ILOŚĆ', P2M+80, y3);
        doc.text('UWAGI', P2M+100, y3);
        y3 += 6;

        cableList.forEach(([type, cableCount], i) => {
          if (i % 2 === 0) { doc.setFillColor(15, 20, 30); doc.rect(P2M, y3-5, P2W-2*P2M, 10, 'F'); }
          // Kolor wg typu
          const isSDI = type.startsWith('SDI');
          const isHDMI = type === 'HDMI';
          const isAudio = ['XLR','Jack','Mini-Jack','Dante','AES-EBU','AES50','MADI-BNC'].includes(type);
          const tc = isSDI ? [59,130,246] : isHDMI ? [249,115,22] : isAudio ? [16,185,129] : [139,92,246];
          doc.setFillColor(...tc);
          doc.rect(P2M, y3-5, 2, 10, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.setTextColor(210, 220, 240);
          doc.text(type, P2M+4, y3);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(...tc);
          doc.text(`×${cableCount.total}`, P2M+80, y3);
          // Uwagi o formacie
          const maxFmt = PORT_MAX_FORMAT[type];
          if (maxFmt) {
            const rank = FORMAT_RANK[maxFmt] ?? 0;
            const prjRank = FORMAT_RANK[project.formatId] ?? 0;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            doc.setTextColor(rank >= prjRank ? 70 : 180, rank >= prjRank ? 160 : 90, rank >= prjRank ? 80 : 40);
            doc.text(cableCount.needed ? `BRAK: ${cableCount.needed} — kup / wypożycz` : (rank >= prjRank ? `✓ dostępne · obsługuje ${projFmt?.label}` : `max ${maxFmt} — niewystarczający`), P2M+100, y3);
          } else {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
            doc.setTextColor(cableCount.needed ? 220 : 70, cableCount.needed ? 80 : 160, cableCount.needed ? 130 : 80);
            doc.text(cableCount.needed ? `BRAK: ${cableCount.needed} — kup / wypożycz` : '✓ dostępne', P2M+100, y3);
          }
          y3 += 11;
        });
      }

      // Stopka na wszystkich stronach
      const totalPages = doc.internal.getNumberOfPages();
      for (let pg = 1; pg <= totalPages; pg++) {
        doc.setPage(pg);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(40, 52, 72);
        doc.text(`GearNode BETA  ·  ${pdfDate}  ·  strona ${pg}/${totalPages}`, P2W/2, P2H - 7, { align: 'center' });
      }

      doc.save(`gearnode-schemat-${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Błąd eksportu PDF: ' + err.message);
    } finally {
      setPdfExporting(false);
    }
  }, [nodes, canvasStats, inventory, project]);

  const getCanvasCoords = useCallback((e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (cx - rect.left - viewTransform.x) / viewTransform.scale,
      y: (cy - rect.top  - viewTransform.y) / viewTransform.scale,
      rawX: cx, rawY: cy,
      overlayX: cx - rect.left, overlayY: cy - rect.top,
    };
  }, [viewTransform]);

  const handleGlobalMove = useCallback((e) => {
    if (!draggingNode && !activeWire && !isPanning) return;
    if (e.cancelable) e.preventDefault();
    const coords = getCanvasCoords(e);
    if (requestRef.current) cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(() => {
      if (draggingNode) {
        setNodes(prev => prev.map(n => n.instanceId === draggingNode.id
          ? { ...n, x: coords.x - draggingNode.ox, y: coords.y - draggingNode.oy } : n));
      } else if (activeWire) {
        // Sprawdź port pod kursorem — pokaż kompatybilność
        const el = document.elementFromPoint(coords.rawX, coords.rawY);
        const portEl = el?.closest('.port-dot');
        let compat = null;
        if (portEl) {
          const nid   = portEl.getAttribute('data-nid');
          const pid   = portEl.getAttribute('data-pid');
          const ptype = portEl.getAttribute('data-ptype');
          if (nid !== activeWire.fnid && ptype !== activeWire.fptype) {
            const ns = nodesRef.current;
            const fromN = ns.find(n => n.instanceId === activeWire.fnid);
            const toN   = ns.find(n => n.instanceId === nid);
            if (fromN && toN) {
              const fromP = (activeWire.fptype === 'in' ? fromN.inputs : fromN.outputs).find(p => p.id === activeWire.fpid);
              const toP   = (ptype === 'in' ? toN.inputs : toN.outputs).find(p => p.id === pid);
              const chk = checkConnection(fromP.type, toP.type, projectRef.current.formatId);
              compat = chk.status;
            }
          }
        }
        setActiveWire(prev => ({ ...prev, cx: coords.x, cy: coords.y, compat }));
      } else if (isPanning) {
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        setViewTransform(prev => ({ ...prev, x: prev.x + (cx - panStart.x), y: prev.y + (cy - panStart.y) }));
        setPanStart({ x: cx, y: cy });
      }
    });
  }, [draggingNode, activeWire, isPanning, panStart, getCanvasCoords]);

  const handleGlobalEnd = (e) => {
    if (activeWire) {
      const coords = getCanvasCoords(e);
      const el = document.elementFromPoint(coords.rawX, coords.rawY);
      const port = el?.closest('.port-dot');
      if (port) {
        const nid   = port.getAttribute('data-nid');
        const pid   = port.getAttribute('data-pid');
        const ptype = port.getAttribute('data-ptype');
        const fromN = nodes.find(n => n.instanceId === activeWire.fnid);
        const toN   = nodes.find(n => n.instanceId === nid);
        if (fromN && toN) {
          const fromP = (activeWire.fptype === 'in' ? fromN.inputs : fromN.outputs).find(p => p.id === activeWire.fpid);
          const toP   = (ptype === 'in' ? toN.inputs : toN.outputs).find(p => p.id === pid);
          if (nid !== activeWire.fnid && ptype !== activeWire.fptype && fromP && toP) {
            const chk = checkConnection(fromP.type, toP.type, project.formatId);
            if (!chk.canConnect) {
              // Niezgodne fizycznie — pokaż błąd
              clearTimeout(connErrorTimer.current);
              setConnError({ visible: true, msg: chk.message, x: coords.rawX, y: coords.rawY });
              connErrorTimer.current = setTimeout(() => setConnError({ visible: false }), 3500);
            } else {
              // Można połączyć (ok lub ostrzeżenie)
              if (chk.status === 'warn') {
                clearTimeout(connErrorTimer.current);
                setConnError({ visible: true, msg: `⚠ ${chk.message}`, x: coords.rawX, y: coords.rawY, isWarn: true });
                connErrorTimer.current = setTimeout(() => setConnError({ visible: false }), 4000);
              }
              const outNode = activeWire.fptype === 'out' ? activeWire.fnid : nid;
              const outPort = activeWire.fptype === 'out' ? activeWire.fpid : pid;
              const inNode  = activeWire.fptype === 'in'  ? activeWire.fnid : nid;
              const inPort  = activeWire.fptype === 'in'  ? activeWire.fpid : pid;
              const builtInCable = nodes.find(node => node.instanceId === outNode)?.outputs?.find(port => port.id === outPort)?.integratedCable;
              const outPortData = nodes.find(node => node.instanceId === outNode)?.outputs?.find(port => port.id === outPort);
              const inPortData = nodes.find(node => node.instanceId === inNode)?.inputs?.find(port => port.id === inPort);
              setHistory(prev => [...prev, { nodes, links }].slice(-20));
              const newLinks = [
                ...links.filter(l => !(l.fromNode === outNode && l.fromPort === outPort) && !(l.toNode === inNode && l.toPort === inPort)),
                { id: `l-${Date.now()}`, fromNode: outNode, fromPort: outPort, toNode: inNode, toPort: inPort, color: '#3b82f6', cableStatus: builtInCable ? 'available' : 'needed', cableIncluded: Boolean(builtInCable), audioTransport: outPortData?.type === 'HDMI' && inPortData?.type === 'HDMI' }
              ];
              setLinks(newLinks);
              saveToCloud(nodes, newLinks);
            }
          }
        }
      }
    }
    if (draggingNode) saveToCloud(nodes, links);
    setDraggingNode(null); setActiveWire(null); setIsPanning(false);
  };

  const getPortPos = useCallback((nid, pid, type) => {
    const node = nodes.find(n => n.instanceId === nid);
    if (!node) return { x: 0, y: 0 };
    const visible = displayPorts(node, links);
    const ports = type === 'in' ? visible.inputs : visible.outputs;
    const idx = ports.findIndex(p => p.id === pid);
    return { x: type === 'in' ? node.x + 30 : node.x + 194, y: node.y + 56 + 16 + (idx * 40) + 20 };
  }, [nodes, links]);

  const toggleNodePorts = useCallback((id) => {
    setHistory(prev => [...prev, { nodes, links }].slice(-20));
    const next = nodes.map(node => node.instanceId === id ? { ...node, portsExpanded: !node.portsExpanded } : node);
    setNodes(next);
    saveToCloud(next, links);
  }, [nodes, links, saveToCloud]);

  const handleDeleteNode = useCallback((id) => {
    if (!window.confirm('Usunąć to urządzenie z bieżącego eventu wraz z jego połączeniami? Sprzęt pozostanie w magazynie.')) return;
    setHistory(prev => [...prev, { nodes, links }].slice(-20));
    const nn = nodes.filter(x => x.instanceId !== id);
    const ll = links.filter(l => l.fromNode !== id && l.toNode !== id);
    setNodes(nn); setLinks(ll); setAssignedAssetIds([...new Set(nn.map(node => node.assetId).filter(Boolean))]); saveToCloud(nn, ll);
  }, [nodes, links, saveToCloud]);

  const handleNodeContext = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    const coords = getCanvasCoords(event);
    setPortMenu({ visible: false });
    setNodeMenu({ visible: true, x: coords.overlayX, y: coords.overlayY, nodeId: node.instanceId });
  }, [getCanvasCoords]);

  const setNodeColor = useCallback((id, color) => {
    const next = nodes.map(node => node.instanceId === id ? { ...node, color } : node);
    setNodes(next);
    saveToCloud(next, links);
    setNodeMenu({ visible: false });
  }, [nodes, links, saveToCloud]);

  const handlePortContext = useCallback((e, node, port, type) => {
    e.preventDefault(); e.stopPropagation();
    const coords = getCanvasCoords(e);
    if (links.some(l => type === 'in'
      ? (l.toNode === node.instanceId && l.toPort === port.id)
      : (l.fromNode === node.instanceId && l.fromPort === port.id))) {
      setPortMenu({ visible: true, x: coords.overlayX, y: coords.overlayY, nodeId: node.instanceId, portId: port.id, type });
    }
  }, [links, getCanvasCoords]);

  const handleLinkClick = useCallback((event, link) => {
    event.stopPropagation();
    const node = nodes.find(item => item.instanceId === link.fromNode);
    const port = node?.outputs?.find(item => item.id === link.fromPort);
    if (node && port) handlePortContext(event, node, port, 'out');
  }, [nodes, handlePortContext]);

  const addNodeToCanvas = useCallback((device, asset) => {
    if (nodes.some(node => node.assetId === asset.assetId)) return;
    setHistory(prev => [...prev, { nodes, links }].slice(-20));
    const n = { ...device, assetId: asset.assetId, assetLabel: asset.label || asset.serialNumber || '', instanceId: `i-${Date.now()}`, x: 120, y: 80 + nodes.length * 20, portsExpanded: false };
    const newNodes = [...nodes, n];
    setNodes(newNodes);
    setAssignedAssetIds([...new Set(newNodes.map(node => node.assetId).filter(Boolean))]);
    saveToCloud(newNodes, links);
  }, [nodes, links, saveToCloud]);

  const portMenuLink = links.find(link => portMenu.type === 'in'
    ? link.toNode === portMenu.nodeId && link.toPort === portMenu.portId
    : link.fromNode === portMenu.nodeId && link.fromPort === portMenu.portId);
  const portMenuCable = (() => {
    if (!portMenuLink) return null;
    const fromNode = nodes.find(node => node.instanceId === portMenuLink.fromNode);
    const toNode = nodes.find(node => node.instanceId === portMenuLink.toNode);
    const fromPort = fromNode?.outputs.find(port => port.id === portMenuLink.fromPort);
    const toPort = toNode?.inputs.find(port => port.id === portMenuLink.toPort);
    return getRequiredCable(fromPort?.type, toPort?.type);
  })();
  const compatibleCables = portMenuCable ? cableInventory.filter(cable =>
    cable.typeId === portMenuCable.id &&
    cable.status === 'available' &&
    (!usedCableIds.has(cable.cableId) || cable.cableId === portMenuLink?.cableId)
  ) : [];

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="h-screen w-screen bg-[#0b0e14] flex flex-col items-center justify-center text-indigo-500 font-black tracking-[0.3em] uppercase">
      Inicjalizacja GearNode...
    </div>
  );

  // ─── Login ──────────────────────────────────────────────────────────────────
  if (!user) return (
    <div className="h-screen w-screen bg-[#0b0e14] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-[#12161f] border border-white/10 p-12 rounded-[60px] shadow-2xl text-center">
        <HardDrive className="text-indigo-600 mx-auto mb-8" size={64} />
        <h1 className="text-4xl font-black text-white uppercase italic mb-10 tracking-tighter">
          GearNode <span className="text-indigo-500 block text-xl not-italic tracking-widest mt-2">BETA</span>
        </h1>
        <button onClick={() => signInWithPopup(auth, new GoogleAuthProvider())}
          className="w-full bg-white text-black py-4 rounded-3xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all active:scale-95 shadow-xl">
          ZALOGUJ PRZEZ GOOGLE
        </button>
        <button onClick={() => signInAnonymously(auth)}
          className="mt-4 w-full bg-slate-800/50 text-slate-400 py-4 rounded-3xl font-bold text-xs hover:bg-slate-800 transition-all">
          TRYB DEMO
        </button>
      </div>
    </div>
  );

  // ─── App ────────────────────────────────────────────────────────────────────
  return (
    <div
      className={`h-screen w-screen bg-[#0b0e14] text-slate-300 overflow-hidden flex flex-col font-sans select-none touch-none ${interactionMode === 'hand' && appView === 'editor' ? 'cursor-grab active:cursor-grabbing' : ''}`}
      onMouseMove={appView === 'editor' ? handleGlobalMove : undefined}
      onTouchMove={appView === 'editor' ? handleGlobalMove : undefined}
      onMouseUp={appView === 'editor' ? handleGlobalEnd : undefined}
      onTouchEnd={appView === 'editor' ? handleGlobalEnd : undefined}
      onClick={() => { setPortMenu({ visible: false }); setNodeMenu({ visible: false }); }}
    >
      {/* ── HEADER ────────────────────────────────────────────────────────── */}
      <header className="h-16 border-b border-white/10 bg-[#12161f]/80 backdrop-blur-md flex justify-between items-center px-6 z-50 shrink-0 shadow-lg">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-3">
            <HardDrive className="text-indigo-500 w-5 h-5" />
            <h1 className="text-sm font-black uppercase tracking-[0.2em] text-white italic">
              GearNode <span className="text-indigo-400 not-italic">BETA</span>
            </h1>
          </div>
          {/* Nawigacja */}
          <nav className="flex items-center gap-1 bg-white/5 rounded-2xl p-1">
            <button
              onClick={() => setAppView('editor')}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                appView === 'editor' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}>
              {t.editor}
            </button>
            <button
              onClick={() => setAppView('inventory')}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                appView === 'inventory' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}>
              <Package size={11} /> {t.inventory}
              {inventory.length > 0 && (
                <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-black ${
                  appView === 'inventory' ? 'bg-white/20' : 'bg-indigo-600 text-white'
                }`}>{inventory.length}</span>
              )}
            </button>
            <button
              onClick={() => setAppView('cables')}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                appView === 'cables' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}>
              <Cable size={11} /> {t.cables}
              {cableInventory.length > 0 && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/20">{cableInventory.length}</span>}
            </button>
          </nav>
          <button onClick={() => setShowEventManager(true)} title="Zmień event lub utwórz nowy"
            className="hidden md:flex items-center gap-2 max-w-[220px] px-3 py-2 rounded-2xl border border-white/5 bg-white/5 hover:bg-white/10 transition-colors">
            <FolderOpen size={14} className="text-indigo-400 shrink-0" />
            <span className="text-[10px] font-black text-slate-300 truncate">{project.name || t.projectFallback}</span>
          </button>
        </div>
        <div className="flex gap-3 items-center">
          {appView === 'editor' && history.length > 0 && (
            <button onClick={() => {
              const prev = history[history.length - 1];
              setHistory(h => h.slice(0, -1));
              setNodes(prev.nodes); setLinks(prev.links);
              saveToCloud(prev.nodes, prev.links);
            }} className="p-2 text-slate-600 hover:text-indigo-400 transition-colors" title="Cofnij (Undo)">
              <RotateCcw size={18} />
            </button>
          )}
          {appView === 'editor' && (
            <button
              onClick={() => setShowProjectModal(true)}
              title="Ustawienia projektu (format wideo)"
              className="flex items-center gap-2 px-3 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white border border-white/5">
              <span className="text-indigo-400 text-xs">▶</span>
              <span className="hidden sm:inline">{PROJECT_FORMATS.find(f => f.id === project.formatId)?.label ?? project.formatId}</span>
            </button>
          )}
          {appView === 'editor' && nodes.length > 0 && (
            <button
              onClick={exportPDF}
              disabled={pdfExporting}
              title="Eksportuj schemat do PDF"
              className={`flex items-center gap-2 px-3 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                pdfExporting
                  ? 'bg-indigo-900/30 text-indigo-600 cursor-wait'
                  : 'bg-indigo-600/20 hover:bg-indigo-600 text-indigo-400 hover:text-white'
              }`}>
              <FileDown size={15} />
              <span className="hidden sm:inline">{pdfExporting ? 'Generuję...' : 'PDF'}</span>
            </button>
          )}
          {syncError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-yellow-500/30 bg-yellow-950/40 text-yellow-300" title={syncError}>
              <CloudOff size={14} />
              <span className="hidden lg:inline text-[9px] font-black uppercase tracking-wider">{t.localSave}</span>
            </div>
          )}
          <div className="flex rounded-xl border border-white/10 bg-white/5 p-1" aria-label="Language">
            {LANGUAGE_OPTIONS.map(option => <button key={option.id} onClick={() => setLanguage(option.id)} className={`rounded-lg px-2 py-1 text-[9px] font-black ${language === option.id ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-white'}`}>{option.label}</button>)}
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-2xl border border-white/5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]" />
            <span className="text-[10px] font-black uppercase text-slate-400 truncate max-w-[120px]">{user.email || 'USER DEMO'}</span>
          </div>
          <button onClick={() => signOut(auth)} className="p-2 text-slate-600 hover:text-red-400 transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {appView === 'editor' && (
        <div className="flex h-11 shrink-0 items-end gap-1 overflow-x-auto border-b border-white/10 bg-[#0e1219] px-5 pt-2">
          {openProjectIds.map(projectId => {
            const item = projects.find(candidate => candidate.id === projectId);
            if (!item) return null;
            const active = projectId === currentProjectId;
            return <div key={projectId} className={`flex h-9 min-w-[150px] max-w-[220px] items-center gap-2 rounded-t-xl border border-b-0 px-3 ${active ? 'border-indigo-500/60 bg-[#171d2a] text-white' : 'border-white/10 bg-white/[.03] text-slate-500'}`}>
              <button onClick={() => selectProject(projectId)} className="min-w-0 flex-1 truncate text-left text-[10px] font-black">{item.name || 'Event bez nazwy'}</button>
              {openProjectIds.length > 1 && <button onClick={() => closeProjectTab(projectId)} className="shrink-0 hover:text-red-400"><X size={12}/></button>}
            </div>;
          })}
          <button onClick={() => setShowEventManager(true)} title="Otwórz lub utwórz event" className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/5 text-slate-500 hover:bg-indigo-600 hover:text-white"><Plus size={14}/></button>
          <span className="mb-2 ml-auto shrink-0 text-[8px] font-bold uppercase tracking-widest text-slate-700">{openProjectIds.length}/5 otwartych</span>
        </div>
      )}

      {/* ── WIDOK: EDYTOR ──────────────────────────────────────────────────── */}
      {appView === 'editor' && (
        <div className="flex-1 flex overflow-hidden">

          {/* Sidebar — tylko Mój Sprzęt */}
          <aside className="w-16 sm:w-64 border-r border-white/10 bg-[#0f1219] flex flex-col z-50 shrink-0 shadow-2xl overflow-hidden">
            <div className="p-3 border-b border-white/10 shrink-0">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 hidden sm:block">Twój sprzęt</p>
            </div>

            {sidebarDevices.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4 text-center">
                <Package size={28} className="text-slate-700" />
                <p className="text-[10px] text-slate-600 font-bold hidden sm:block">Brak sprzętu</p>
                <button onClick={() => setAppView('inventory')}
                  className="text-[9px] text-indigo-500 hover:text-indigo-300 font-black uppercase tracking-widest transition-colors hidden sm:block">
                  → Dodaj sprzęt
                </button>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {sidebarDevices.map(asset => {
                  const { device } = asset;
                  const placed = nodes.some(node => node.assetId === asset.assetId);
                  const conflict = assetConflicts.has(asset.assetId);
                  return (
                  <button key={asset.assetId}
                    onClick={() => addNodeToCanvas(device, asset)} disabled={placed || conflict}
                    title={conflict ? 'Ten egzemplarz jest używany w innym evencie tego samego dnia' : ''}
                    className={`w-full border p-3 rounded-2xl transition-all flex flex-col items-center sm:items-start group text-left shadow-sm ${placed || conflict ? 'bg-[#12161f] border-white/5 opacity-45 cursor-not-allowed' : 'bg-[#1a1f2b] border-white/5 hover:border-indigo-500'}`}>
                    <Plus size={18} className="text-indigo-400 sm:hidden" />
                    <div className="hidden sm:flex sm:items-start sm:justify-between sm:w-full gap-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-[8px] font-bold text-indigo-500 uppercase tracking-tighter truncate">{device.brand}</p>
                        <p className="text-[10px] font-bold text-white leading-none mt-1 group-hover:text-indigo-300 transition-colors truncate">{device.model}</p>
                      </div>
                      <span className="shrink-0 text-[8px] bg-white/10 text-slate-400 rounded-lg px-1.5 py-0.5 font-black max-w-[80px] truncate">{placed ? 'NA PLANIE' : conflict ? 'KONFLIKT' : (asset.serialNumber || 'EGZEMPLARZ')}</span>
                    </div>
                  </button>
                );})}
              </div>
            )}
          </aside>

          {/* Canvas */}
          <main
            className="flex-1 relative bg-[#0b0e14] overflow-hidden"
            ref={canvasRef}
            onMouseDown={(e) => {
              if (e.target === canvasRef.current || interactionMode === 'hand') {
                setIsPanning(true); setPanStart({ x: e.clientX, y: e.clientY }); setPortMenu({ visible: false }); setNodeMenu({ visible: false });
              }
            }}
          >
            {/* ── Pasek statystyk (Faza 3) ─────────────────────────────── */}
            {(canvasStats.nodeCount > 0) && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#12161f]/90 border border-white/10 backdrop-blur-xl rounded-[32px] px-5 py-3 shadow-2xl">
                {/* Urządzenia */}
                <div className="flex items-center gap-2 pr-3 border-r border-white/10">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Urządzeń</span>
                  <span className="text-[13px] font-black text-white">{canvasStats.nodeCount}</span>
                </div>
                {/* Moc */}
                <div className="flex items-center gap-2 pr-3 border-r border-white/10">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Moc</span>
                  <span className={`text-[13px] font-black ${canvasStats.power > 0 ? 'text-yellow-400' : 'text-slate-500'}`}>
                    {canvasStats.power > 0 ? `${canvasStats.power}W` : '—'}
                  </span>
                </div>
                {/* Kable */}
                {canvasStats.linkCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Kable</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {Object.entries(canvasStats.cables).map(([type, cableCount]) => (
                        <span key={type}
                          className={`text-[9px] font-black px-2 py-0.5 rounded-lg border ${cableCount.needed ? 'bg-pink-950/70 text-pink-300 border-pink-700/50' : 'bg-emerald-950/60 text-emerald-300 border-emerald-700/40'}`}>
                          {type} ×{cableCount.total}{cableCount.needed ? ` · brak ${cableCount.needed}` : ' · mam'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Kontrolki zoom */}
            <div className="absolute bottom-8 right-8 flex flex-col gap-3 z-40 bg-slate-900/80 p-3 rounded-[32px] border border-white/10 backdrop-blur-xl shadow-2xl">
              <button onClick={() => setInteractionMode('select')} className={`p-3 rounded-2xl transition-all ${interactionMode === 'select' ? 'bg-indigo-600 text-white' : 'hover:bg-white/10 text-slate-400'}`}><MousePointer2 size={22} /></button>
              <button onClick={() => setInteractionMode('hand')}   className={`p-3 rounded-2xl transition-all ${interactionMode === 'hand'   ? 'bg-indigo-600 text-white' : 'hover:bg-white/10 text-slate-400'}`}><Hand size={22} /></button>
              <div className="h-px bg-white/10 my-1 mx-2" />
              <button onClick={() => setViewTransform(v => ({ ...v, scale: Math.min(v.scale + 0.1, 2.5) }))} className="p-3 hover:bg-white/10 rounded-2xl text-white"><ZoomIn size={20} /></button>
              <button onClick={() => setViewTransform(v => ({ ...v, scale: Math.max(v.scale - 0.1, 0.2) }))} className="p-3 hover:bg-white/10 rounded-2xl text-white"><ZoomOut size={20} /></button>
            </div>

            {/* Canvas world */}
            <div
              style={{ transform: `translate(${viewTransform.x}px, ${viewTransform.y}px) scale(${viewTransform.scale})`, transformOrigin: '0 0' }}
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              <div className="absolute inset-[-5000px] bg-[radial-gradient(#1e222d_1.5px,transparent_1.5px)] bg-[size:40px_40px] pointer-events-none opacity-30" />
              <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                {links.map(l => <MemoLink key={l.id} link={l} getPortPos={getPortPos} warning={linkWarnings[l.id]} missingCable={l.cableStatus !== 'available'} onClick={handleLinkClick} />)}
                {activeWire && (
                  <path
                    d={`M ${activeWire.sx} ${activeWire.sy} L ${activeWire.cx} ${activeWire.cy}`}
                    stroke={activeWire.compat === 'ok' ? '#22c55e' : activeWire.compat === 'err' ? '#ef4444' : activeWire.compat === 'warn' ? '#f59e0b' : '#6366f1'}
                    strokeWidth={activeWire.compat ? 4 : 3}
                    strokeDasharray={activeWire.compat ? '5,3' : '8,4'}
                    fill="none"
                    className={activeWire.compat === 'err' ? 'opacity-70' : 'opacity-50'}
                  />
                )}
              </svg>
              <div className={`relative w-full h-full ${interactionMode === 'select' ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                {nodes.map(n => (
                  <MemoNode
                    key={n.instanceId} n={n}
                    isDragging={draggingNode?.id === n.instanceId}
                    links={links}
                    language={language}
                    onMouseDown={(e, node) => {
                      e.stopPropagation();
                      const c = getCanvasCoords(e);
                      setDraggingNode({ id: node.instanceId, ox: c.x - node.x, oy: c.y - node.y });
                    }}
                    onPortMouseDown={(node, port, type, e) => {
                      e.stopPropagation();
                      const c = getCanvasCoords(e);
                      setActiveWire({ fnid: node.instanceId, fpid: port.id, fptype: type, sx: c.x, sy: c.y, cx: c.x, cy: c.y });
                    }}
                    onPortContext={handlePortContext}
                    onNodeContext={handleNodeContext}
                    onTogglePorts={toggleNodePorts}
                  />
                ))}
              </div>
            </div>

            {nodeMenu.visible && (
              <div className="absolute z-[110] w-56 rounded-[28px] border border-white/20 bg-slate-900 p-4 shadow-2xl" style={{ left: nodeMenu.x, top: nodeMenu.y }} onClick={event => event.stopPropagation()}>
                <p className="mb-3 text-center text-[9px] font-black uppercase tracking-widest text-white">Opcje urządzenia</p>
                <p className="mb-2 text-[8px] font-black uppercase tracking-widest text-slate-500">Kolor modułu</p>
                <div className="mb-4 grid grid-cols-6 gap-2">{CABLE_COLORS.map(color => <button key={color.value} onClick={() => setNodeColor(nodeMenu.nodeId, color.value)} className="h-7 w-7 rounded-full border border-white/20 transition-transform hover:scale-110" style={{ backgroundColor: color.value }} title={color.name}/>)}</div>
                <button onClick={() => { const id = nodeMenu.nodeId; setNodeMenu({ visible: false }); handleDeleteNode(id); }} className="flex w-full items-center justify-between rounded-2xl p-3 text-[10px] font-black uppercase tracking-widest text-red-400 hover:bg-red-500/10">Usuń z eventu <Eraser size={14}/></button>
              </div>
            )}

            {/* Port menu (kolor kabla / usuń) */}
            {portMenu.visible && (
              <div
                className="absolute z-[100] bg-slate-900 border border-white/20 rounded-[32px] shadow-2xl p-5 w-64"
                style={{ left: portMenu.x, top: portMenu.y }}
                onClick={(e) => e.stopPropagation()}
              >
                {portMenuCable && (
                  <div className="mb-4 rounded-2xl bg-black/25 border border-white/10 p-3">
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-500">Potrzebny przewód</p>
                    <p className="mt-1 text-xs font-black text-white">{portMenuCable.label}</p>
                    {portMenuLink && linkWarnings[portMenuLink.id] && <p className="mt-2 rounded-lg bg-amber-950/60 p-2 text-[9px] font-bold text-amber-300">⚠ {linkWarnings[portMenuLink.id]}</p>}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          const nextLinks = links.map(link => link.id === portMenuLink.id ? { ...link, cableStatus: 'available', cableId: null } : link);
                          setLinks(nextLinks); saveToCloud(nodes, nextLinks); setPortMenu({ visible: false });
                        }}
                        className={`rounded-xl px-2 py-2 text-[8px] font-black uppercase ${portMenuLink?.cableStatus === 'available' ? 'bg-emerald-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-emerald-900/40'}`}>
                        Kabel spoza magazynu
                      </button>
                      <button
                        onClick={() => {
                          const nextLinks = links.map(link => link.id === portMenuLink.id ? { ...link, cableStatus: 'needed', cableId: null } : link);
                          setLinks(nextLinks); saveToCloud(nodes, nextLinks); setPortMenu({ visible: false });
                        }}
                        className={`rounded-xl px-2 py-2 text-[8px] font-black uppercase ${portMenuLink?.cableStatus !== 'available' ? 'bg-pink-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-pink-900/40'}`}>
                        Do zdobycia
                      </button>
                    </div>
                    <p className="mt-3 text-[8px] font-black uppercase tracking-widest text-slate-500">Przypisz z magazynu</p>
                    <div className="mt-2 max-h-28 space-y-1 overflow-auto">
                      {compatibleCables.length ? compatibleCables.map(cable => (
                        <button key={cable.cableId} onClick={() => {
                          const nextLinks = links.map(link => link.id === portMenuLink.id ? { ...link, cableStatus: 'available', cableId: cable.cableId } : link);
                          setLinks(nextLinks); saveToCloud(nodes, nextLinks); setPortMenu({ visible: false });
                        }} className={`w-full rounded-xl border px-2 py-2 text-left text-[9px] font-bold ${portMenuLink?.cableId === cable.cableId ? 'border-emerald-500 bg-emerald-950/50 text-emerald-300' : 'border-white/5 bg-white/5 text-slate-300 hover:bg-white/10'}`}>
                          {cable.label}{cable.lengthM ? ` · ${cable.lengthM} m` : ''}
                        </button>
                      )) : <p className="py-2 text-[9px] text-slate-600">Brak dostępnego egzemplarza tego typu.</p>}
                    </div>
                  </div>
                )}
                <p className="text-[9px] font-black text-white uppercase mb-4 text-center tracking-widest">Zmień kolor połączenia</p>
                <div className="grid grid-cols-6 gap-2 mb-5">
                  {CABLE_COLORS.map(c => (
                    <button key={c.value}
                      onClick={() => {
                        const nextLinks = links.map(l =>
                          (portMenu.type === 'in'
                            ? (l.toNode === portMenu.nodeId && l.toPort === portMenu.portId)
                            : (l.fromNode === portMenu.nodeId && l.fromPort === portMenu.portId))
                          ? { ...l, color: c.value } : l
                        );
                        setLinks(nextLinks);
                        saveToCloud(nodes, nextLinks);
                        setPortMenu({ visible: false });
                      }}
                      className="w-7 h-7 rounded-full border border-white/20 hover:scale-110 transition-all shadow-md"
                      style={{ backgroundColor: c.value }}
                    />
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (!window.confirm('Usunąć to połączenie z bieżącego eventu?')) return;
                    const nextLinks = links.filter(l =>
                      portMenu.type === 'in'
                        ? !(l.toNode === portMenu.nodeId && l.toPort === portMenu.portId)
                        : !(l.fromNode === portMenu.nodeId && l.fromPort === portMenu.portId)
                    );
                    setLinks(nextLinks);
                    saveToCloud(nodes, nextLinks);
                    setPortMenu({ visible: false });
                  }}
                  className="w-full flex items-center justify-between p-4 text-red-400 hover:bg-red-500/10 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-colors">
                  USUŃ POŁĄCZENIE <Eraser size={14} />
                </button>
              </div>
            )}
            {/* Toast: błąd połączenia */}
            {connError.visible && (
              <div
                className="absolute z-[200] pointer-events-none"
                style={{ left: connError.x, top: connError.y - 60, transform: 'translateX(-50%)' }}
              >
                <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-wider px-4 py-2.5 rounded-2xl shadow-2xl whitespace-nowrap ${
                  connError.isWarn
                    ? 'bg-yellow-950 border border-yellow-500/50 text-yellow-300'
                    : 'bg-red-950 border border-red-500/50 text-red-300'
                }`}>
                  <span>{connError.isWarn ? '⚠' : '✕'}</span> {connError.msg}
                </div>
              </div>
            )}
          </main>
        </div>
      )}

      {/* ── WIDOK: INVENTORY ───────────────────────────────────────────────── */}
      {appView === 'inventory' && (
        <WarehouseView
          inventory={inventory}
          catalog={combinedCatalog}
          onUpdateInventory={handleUpdateInventory}
          onCreateCustomDevice={addWarehouseDevice}
          protectedAssetIds={assignedAnywhere}
          language={language}
          onOpenCables={() => setAppView('cables')}
          requestedAssetCode={requestedAssetCode}
        />
      )}
      {appView === 'cables' && (
        <CableInventoryView cables={cableInventory} usedCableIds={usedCableIds} onUpdate={handleUpdateCableInventory} language={language} />
      )}

      {/* ── Modal: Projekt ─────────────────────────────────────────────────── */}
      {showProjectModal && (
        <ProjectModal
          project={project}
          onSave={(p) => {
            setProject(p);
            saveToCloud(nodes, links, { project: p });
          }}
          onClose={() => setShowProjectModal(false)}
          language={language}
        />
      )}

      {showEventManager && (
        <EventManagerModal
          projects={projects}
          currentProjectId={currentProjectId}
          onSelect={selectProject}
          onCreate={handleCreateProject}
          onClose={() => setShowEventManager(false)}
          language={language}
        />
      )}

      {/* ── Ukryty widok eksportu (html2canvas) ────────────────────────────── */}
      <SchemaExportView
        nodes={nodes}
        links={links}
        linkWarnings={linkWarnings}
        containerRef={schemaExportRef}
      />
    </div>
  );
};

export default App;
